<script setup lang="ts">
import BaseIcon from '@/components/ui/BaseIcon.vue';
import BookmarkButton from '@/components/ui/BookmarkButton.vue';
import BottomSheet, { type BottomSheetPosition } from '@/components/layout/BottomSheet.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import Tag, { type TagElement } from '@/components/ui/InfoTag.vue';
import TripStopRow from './TripStopRow.vue';
import { collapseDrawer, expandDrawer, releaseDrawerHeight } from '@/libs/helpers/drawer-transition';
import { findNearestStop } from '@/libs/helpers/nearby-stop';
import { AppRoute } from '@/libs/routing';
import { fadedTimelineColor, timelineColor } from '@/libs/route-color';
import { getCrowdingRatioTag, formatTags } from '@/libs/tags-helper';
import { useClock } from '@/use/clock';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { Vehicle } from '@/@types/vehicle';
import type { Route, Shape, Stop, StopTimeData, Trip } from '@/@types/gtfs-ressource';
import type { OnlineDevice } from '@/@types/device';
import { type FormatedUpdate, StopType, type StopTimeWithType, type TripUpdate } from '@/@types/trip-updates';
import { useGtfsStore, type Bounds } from '@/stores/gtfs';
import { useDevicesStore } from '@/stores/devices';
import { useTripUpdatesStore } from '@/stores/trip-updates';
import { MapFocusType, useAppStore } from '@/stores/app';
import type { TripDetailRow } from './types';
import {
  buildDeviceMarkers,
  buildNextDepartures,
  splitTripRows,
  type BusState,
  type NextDeparture,
  type TripRowsSplit,
} from './trip-rows';

const { d } = useI18n();

const gtfsStore = useGtfsStore();
const devicesStore = useDevicesStore();
const tripUpdatesStore = useTripUpdatesStore();
const appStore = useAppStore();
const router = useRouter();

const props = defineProps<{
  tripId: string;
}>();

// Opens on the reading snap: the detail is what the passenger came for, the map is context.
const sheetPosition = ref<BottomSheetPosition>('intermediate');
const panelEl = ref<HTMLElement>();
const hasScrolledToVehicle = ref<boolean>(false);
const isMoreTimesOpen = ref<boolean>(false);

const clockNow = useClock();

const routes = computed<{ [routeId: string]: Route }>(() => gtfsStore.gtfsData.routes);
const shapes = computed<{ [shapeId: string]: Shape }>(() => gtfsStore.gtfsData.shapes);
const stops = computed<{ [stopId: string]: Stop }>(() => gtfsStore.gtfsData.stops);
const vehicles = computed<Array<Vehicle>>(() => devicesStore.vehicles);
// Trip object, only when running now, with a `midnight` property: a night trip of the day before,
// or a trip of the day before a late vehicle still runs, as the departures show it (#303).
const trip = computed<Trip | null>(() => gtfsStore.runningTrips(clockNow.value)?.[props.tripId]);

const route = computed<Route>(() => (trip.value && routes.value[trip.value.route_id]) || ({} as Route));

const lineColor = computed<string>(() => timelineColor(route.value.route_color));
const lineColorFaded = computed<string>(() => fadedTimelineColor(lineColor.value));

// Only the vehicles running this occurrence of the trip, by their service day (#303).
const devices = computed<{ [deviceId: string]: OnlineDevice }>(() =>
  gtfsStore
    .devicesOnTrip(props.tripId, clockNow.value)
    .reduce((acc: { [deviceId: string]: OnlineDevice }, d) => {
      acc[d.device_id] = d;
      return acc;
    }, {}),
);

const bestDevice = computed<OnlineDevice | null>(() => {
  // Find most advanced on shape.
  return Object.values(devices.value).reduce((best: OnlineDevice | null, device) => {
    if (
      !best ||
      (device.shape_dist_traveled != null &&
        best.shape_dist_traveled != null &&
        device.shape_dist_traveled > best.shape_dist_traveled)
    ) {
      return device;
    } else {
      return best;
    }
  }, null);
});

const isRealtime = computed<boolean>(() => bestDevice.value !== null);

// Rebuilt by the watcher below, on a new trip or trip update only.
const feedStopTimes = shallowRef<Array<StopTimeWithType>>([]);

const formattedStopTimes = computed<Array<TripDetailRow>>(() => {
  if (!trip.value) return [];
  return feedStopTimes.value.map(st => {
    const thTime = trip.value!.midnight + st.departure_time;
    const stop = stops.value[st.stop_id];
    const tags = formatTags(stop, st);

    let thTimeBase = thTime * 1000;
    let timeWarning = false;
    if (bestDevice.value) {
      // Vehicle connected: estimated time
      thTimeBase = (thTime + (bestDevice.value.delay ?? 0)) * 1000;
    } else {
      const specificStopDelay = tripUpdateForTrip.value?.delays?.find(
        dt => dt.stop_sequence === st.stop_sequence,
      )?.delay;
      if (tripUpdateForTrip.value?.delay || specificStopDelay) {
        thTimeBase = (thTime + (specificStopDelay ?? 0) + (tripUpdateForTrip.value?.delay ?? 0)) * 1000;
        timeWarning = true;
      }
    }

    return {
      stopId: st.stop_id,
      stopSequence: st.stop_sequence,
      name: st.name,
      type: st.type,
      tags,
      time: d(thTimeBase, 'timeShort'),
      timeWarning,
      thTimeBase,
    };
  });
});

// Rows split around the vehicle position: served stops greyed out, next stop
// promoted to the enriched current row.
const rowsSplit = computed<TripRowsSplit>(() =>
  splitTripRows(formattedStopTimes.value, bestDevice.value?.current_stop_sequence),
);

const passedStopTimes = computed<Array<TripDetailRow>>(() => rowsSplit.value.passed);
const passedRow = computed<TripDetailRow | undefined>(() => rowsSplit.value.passedRow);
const upcomingStopTimes = computed<Array<TripDetailRow>>(() => rowsSplit.value.upcoming);
const currentRow = computed<TripDetailRow | undefined>(() => rowsSplit.value.currentRow);

// The first upcoming row wears the enriched "current" block, unless that stop is canceled
const hasEnrichedCurrentRow = computed<boolean>(
  () => !!currentRow.value && currentRow.value.type !== StopType.CANCELED,
);

const passedAgoMinutes = computed<number>(() =>
  passedRow.value ? Math.max(1, Math.round((clockNow.value * 1000 - passedRow.value.thTimeBase) / 60000)) : 0,
);

const plannedInMinutes = computed<number>(() =>
  currentRow.value
    ? Math.max(1, Math.round((currentRow.value.thTimeBase - clockNow.value * 1000) / 60000))
    : 0,
);

// One marker per vehicle, keyed by the row that hosts it: a vehicle lagging
// behind the most advanced one stays on its own row of the timeline.
const deviceMarkers = computed<Map<number, BusState>>(() =>
  buildDeviceMarkers(Object.values(devices.value), formattedStopTimes.value),
);

// The rows' times already carry the trip-update delays: with no vehicle
// connected, they are the only sign the trip is still running late.
const tripEndTs = computed<number>(
  () => formattedStopTimes.value[formattedStopTimes.value.length - 1]?.thTimeBase ?? 0,
);

const isEndOfService = computed<boolean>(
  () => !!trip.value && !isRealtime.value && tripEndTs.value > 0 && clockNow.value * 1000 > tripEndTs.value,
);

// Never triggers a prompt: the position is only read when already granted
const nearestStopId = computed<string | null>(() => {
  const position = appStore.positionGeolocation;
  if (!position) return null;
  const tripStops = formattedStopTimes.value.map(st => stops.value[st.stopId]).filter(stop => !!stop);
  return findNearestStop(position, tripStops)?.stop_id ?? null;
});

// Kept apart from the 5s clock: getStopTimes walks every trip of the day, and
// only the "now" bound moves between two ticks.
const currentStopStopTimes = computed<StopTimeData[]>(() => {
  const row = currentRow.value;
  if (!row) return [];
  return gtfsStore.getStopTimes(gtfsStore.stations[row.stopId] || [row.stopId]);
});

// Next departures of the same route and direction at the current stop
const nextDepartures = computed<Array<NextDeparture>>(() => {
  if (!currentRow.value || !trip.value) return [];
  return buildNextDepartures(currentStopStopTimes.value, {
    routeId: trip.value.route_id,
    tripHeadsign: trip.value.trip_headsign,
    excludedTripId: props.tripId,
    nowTs: clockNow.value,
  });
});

const tripTags = computed<Array<TagElement>>(() => {
  if (!trip.value) return [];

  const tags = formatTags(undefined, undefined, formattedTripUpdate.value, trip.value);
  // Add crowding tags to previous ones
  const crowdingTags = Object.values(devices.value).map(device => getCrowdingTagForDevice(device));
  if (crowdingTags) tags.push(...crowdingTags.filter(crowdingTag => crowdingTag !== null));
  return tags;
});

const tripUpdateForTrip = computed<TripUpdate | undefined>(() =>
  tripUpdatesStore.getTripUpdateByTripId(props.tripId, trip.value?.service_date),
);
/**
 * trip Updates, additionnal data (delay, trip cancelled, stop unserved)
 */
const formattedTripUpdate = computed<FormatedUpdate | undefined>(() => {
  const tripUpdateFormated = tripUpdateForTrip.value
    ? tripUpdatesStore.formatTripUpdate(tripUpdateForTrip.value, isRealtime.value)
    : undefined;
  return tripUpdateFormated;
});

watch(
  () => formattedTripUpdate.value,
  () => {
    tripUpdatesStore.setCurrentTripUpdateFormatted(formattedTripUpdate.value);
  },
  { immediate: true },
);

// Repopulates the trip-updates store's stop maps, which feed the trip's stops and
// deviations to the map: kept off the vehicle polls, which would redraw them for nothing.
watch(
  [trip, stops, tripUpdateForTrip],
  ([trip, stops, tripUpdate]) => {
    if (!trip) return;
    feedStopTimes.value =
      tripUpdatesStore.initFeedStopTimesAndStops(stops, trip.stop_times, tripUpdate) ?? [];
  },
  { immediate: true },
);

watch(
  () => trip.value,
  () => {
    showShapes();
  },
);

// The page is reused from one trip to the next (the next-departure chips): the
// new trip scrolls to its own vehicle. Declared before the vehicle watcher so
// the reset runs first when both change in the same tick.
watch(
  () => props.tripId,
  () => {
    hasScrolledToVehicle.value = false;
  },
);

watch(
  () => bestDevice.value,
  async () => {
    // scroll to vehicle only once when realtime device is detected, then let the user scroll
    if (hasScrolledToVehicle.value || !bestDevice.value) return;
    hasScrolledToVehicle.value = true;
    await nextTick();
    panelEl.value?.querySelector('.trip-panel__current-target')?.scrollIntoView({ block: 'start' });
  },
);

// A new current stop starts with its extra departures collapsed
watch(
  () => currentRow.value?.stopId,
  () => {
    isMoreTimesOpen.value = false;
  },
);

onMounted(() => {
  showShapes();
  appStore.getGeolocationIfAllowed();
});

onBeforeUnmount(() => {
  tripUpdatesStore.$resetCurrentTripUpdateFormatted();
});

function showShapes() {
  if (!trip.value) return;

  gtfsStore.setShownShapes({
    [trip.value.shape_id]: shapes.value[trip.value.shape_id],
  });

  const bounds: Partial<Bounds> = {};
  if (trip.value.shape_id && shapes.value[trip.value.shape_id]) {
    shapes.value[trip.value.shape_id].geometry.coordinates.forEach(coordinate => {
      if (!bounds.south || coordinate[1] < bounds.south) bounds.south = coordinate[1];
      if (!bounds.north || coordinate[1] > bounds.north) bounds.north = coordinate[1];
      if (!bounds.west || coordinate[0] < bounds.west) bounds.west = coordinate[0];
      if (!bounds.east || coordinate[0] > bounds.east) bounds.east = coordinate[0];
    });
  }

  if (bounds.north != null && bounds.south != null && bounds.east != null && bounds.west != null) {
    appStore.setMapFocus({
      type: MapFocusType.BOUNDS,
      elem: bounds as Bounds,
    });
  }
}

/**
 * Returns the crowding tag for a given device based on its vehicle's load.
 */
function getCrowdingTagForDevice(device: OnlineDevice): TagElement | null {
  if (!device || !device.last_vehicle_associated) return null;
  const vehicle = vehicles.value.find(v => v.id === device.last_vehicle_associated);
  if (!vehicle || !vehicle.capacity) return null;
  return getCrowdingRatioTag(device, vehicle.capacity);
}

function goToStop(stopId: string) {
  // Ad hoc stops don't exist in the GTFS: there is no detail page for them
  if (!stops.value[stopId]) return;
  router.push({ name: AppRoute.STOP_DETAILED, params: { stopId } });
}

function onSheetHeight(height: number, dragging: boolean) {
  appStore.setMapBottomInset(height, dragging);
}
</script>

<template>
  <div
    ref="panelEl"
    class="trip-panel"
    :style="{
      '--timeline-color': isEndOfService ? undefined : lineColor,
      '--timeline-color-faded': lineColorFaded,
    }"
  >
    <BottomSheet v-model:position="sheetPosition" @update:height="onSheetHeight">
      <template #header>
        <div v-if="trip" class="trip-panel__header">
          <div class="trip-panel__identity">
            <div class="trip-panel__titles">
              <div class="trip-panel__line-row">
                <RouteBadge :route="route" />
                <span class="trip-panel__direction">
                  <BaseIcon name="arrow-right" class="trip-panel__direction-icon" />
                  {{ $t('trip.direction') }}
                </span>
              </div>
              <div class="trip-panel__name">{{ trip.trip_headsign }}</div>
            </div>
            <span class="trip-panel__bookmark">
              <BookmarkButton :element="route" />
            </span>
          </div>

          <div v-if="tripTags.length > 0" class="trip-panel__tags">
            <Tag v-for="tag in tripTags" :key="tag.tagType" :tag-type="tag.tagType" :tag-data="tag.tagData" />
          </div>

          <div v-if="isEndOfService" class="trip-panel__status">
            <BaseIcon name="moon" class="trip-panel__status-icon" />
            <span class="trip-panel__status-text">{{ $t('trip.serviceEndedToday') }}</span>
          </div>
        </div>
      </template>

      <div v-if="trip" class="trip-panel__timeline">
        <TripStopRow
          v-for="(row, index) in passedStopTimes"
          :key="row.stopSequence"
          :bus="deviceMarkers.get(row.stopSequence) ?? null"
          :first="index === 0"
          :row="row"
          :sub-label="
            index === passedStopTimes.length - 1
              ? $tc('trip.passedAgo', passedAgoMinutes, { n: passedAgoMinutes })
              : undefined
          "
          variant="passed"
          @click="goToStop(row.stopId)"
        />

        <TripStopRow
          v-for="(row, index) in upcomingStopTimes"
          :key="row.stopSequence"
          :bus="deviceMarkers.get(row.stopSequence) ?? null"
          :class="{ 'trip-panel__current-target': index === 0 }"
          :ended="isEndOfService"
          :faded-lead="!!passedRow && index === 0"
          :first="!passedRow && index === 0"
          :last="index === upcomingStopTimes.length - 1"
          :nearby="row.stopId === nearestStopId"
          :row="row"
          :sub-label="
            hasEnrichedCurrentRow && index === 0
              ? $tc('trip.plannedIn', plannedInMinutes, { n: plannedInMinutes })
              : undefined
          "
          :variant="hasEnrichedCurrentRow && index === 0 ? 'current' : 'regular'"
          @click="goToStop(row.stopId)"
        >
          <template v-if="hasEnrichedCurrentRow && index === 0 && nextDepartures.length > 0" #details>
            <div class="trip-panel__more">
              <button
                :aria-expanded="isMoreTimesOpen"
                class="trip-panel__more-toggle"
                @click.stop="isMoreTimesOpen = !isMoreTimesOpen"
              >
                <BaseIcon
                  name="chevron-down"
                  class="trip-panel__more-chevron"
                  :class="{ 'trip-panel__more-chevron--open': isMoreTimesOpen }"
                />
                {{ $t('trip.showMoreTimes') }}
              </button>

              <Transition
                name="trip-panel-more"
                @enter="expandDrawer"
                @afterEnter="releaseDrawerHeight"
                @leave="collapseDrawer"
              >
                <div v-if="isMoreTimesOpen" class="trip-panel__more-drawer">
                  <div class="trip-panel__more-times">
                    <router-link
                      v-for="departure in nextDepartures"
                      :key="departure.tripId"
                      class="trip-panel__more-chip"
                      :to="{ name: AppRoute.TRIP_DETAILED, params: { tripId: departure.tripId } }"
                      @click.stop
                    >
                      {{ departure.label }}
                    </router-link>
                  </div>
                </div>
              </Transition>
            </div>
          </template>
        </TripStopRow>
      </div>
    </BottomSheet>
  </div>
</template>

<style lang="scss">
.trip-panel {
  @include sheet-host;

  // Fin de service: the whole timeline goes grey
  --timeline-color: #{$color-neutral-medium};
  --timeline-color-faded: #{$color-neutral-lower};

  &__header {
    border-bottom: 1px solid $color-border-light;
    display: flex;
    flex-direction: column;
    gap: $spacing-3;
    padding: $spacing-4 $spacing-4 $spacing-5;
  }

  &__identity {
    align-items: flex-start;
    display: flex;
    gap: $spacing-4;
  }

  &__titles {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
  }

  &__line-row {
    align-items: center;
    display: flex;
    gap: 5px;
  }

  &__direction {
    align-items: center;
    color: $color-text-dark;
    display: flex;
    font: $text-bold-medium;
    gap: $spacing-1;
    letter-spacing: $text-bold-medium-spacing;
  }

  &__direction-icon {
    color: $color-text-neutral;
    font-size: 16px;
  }

  &__name {
    color: $color-neutral-highest;
    display: -webkit-box;
    font: 600 24px / 32px $font-poppins;
    letter-spacing: $letter-spacing-tight;
    // A very long trip_headsign wraps onto 2 lines, then clips
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }

  &__bookmark {
    @include icon-pill;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-2;
  }

  &__status {
    align-items: center;
    display: flex;
    gap: $spacing-1;
  }

  &__status-icon {
    color: $color-secondary;
    flex-shrink: 0;
    font-size: 20px;
  }

  &__status-text {
    color: $color-neutral-higher;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
  }

  &__timeline {
    display: flex;
    flex-direction: column;
    padding-bottom: $spacing-6;
  }

  // Keeps a sliver of the passed summary row visible above the auto-scroll target
  &__current-target {
    scroll-margin-top: 24px;
  }

  &__more {
    display: flex;
    flex-direction: column;
    padding-top: $spacing-2;
  }

  &__more-toggle {
    align-items: center;
    background: none;
    border: none;
    color: $color-neutral-highest;
    cursor: pointer;
    display: flex;
    font: $label-medium;
    gap: $spacing-1;
    letter-spacing: $label-medium-spacing;
    padding: $spacing-2 0;
  }

  &__more-chevron {
    font-size: 24px;
    transition: transform $duration-drawer-open $easing-drawer;

    &--open {
      transform: rotate(180deg);
    }
  }

  // What animates: with no padding of its own it collapses to exactly 0, and
  // the chips' offset from the toggle stays inside it.
  &__more-drawer {
    overflow: hidden;
  }

  &__more-times {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-3;
    padding-top: $spacing-2;
  }

  &__more-chip {
    background-color: $color-neutral-lower;
    border-radius: $radius-card;
    color: $color-neutral-highest;
    font: $text-bold-medium;
    letter-spacing: $text-bold-medium-spacing;
    padding: $spacing-3 $spacing-4;

    &:active {
      background-color: $color-neutral-low;
    }
  }
}

.trip-panel-more-enter-active {
  transition:
    height $duration-drawer-open $easing-drawer,
    opacity $duration-drawer-open $easing-drawer;
}

.trip-panel-more-leave-active {
  transition:
    height $duration-drawer-close $easing-drawer,
    opacity $duration-drawer-close $easing-drawer;
}

.trip-panel-more-enter-from,
.trip-panel-more-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .trip-panel__more-chevron,
  .trip-panel-more-enter-active,
  .trip-panel-more-leave-active {
    transition: none;
  }
}
</style>
